import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  CircularProgress,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Divider,
  Chip,
  Alert,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import WooCommerceProductDetails from "./WooCommerceProductDetailsAnalysisStoc";
import LabelIcon from "@mui/icons-material/Label";
import CardIcon from "@mui/icons-material/CardGiftcard"; // Import the icon
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BoltIcon from "@mui/icons-material/Bolt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CollectionsIcon from "@mui/icons-material/Collections";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { saveAs } from "file-saver";
import calculateCost from "./calculateCostAnalysiSToc";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const VC_IMAGE_ZIP_API_BASE_URL =
  process.env.REACT_APP_VC_IMAGE_ZIP_API_BASE_URL ||
  "https://server-9p4hx.kinsta.app";

const formatNumber = (number) => {
  if (number === null || number === undefined || Number.isNaN(Number(number))) {
    return "0";
  }
  return Number(number).toLocaleString("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "N/A";
  }
  return `${Number(value).toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} RON`;
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("ro-RO") : "N/A";

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("ro-RO", { hour12: false }) : "N/A";

const formatPercent = (value, digits = 1) =>
  value || value === 0 ? `${(Number(value) * 100).toFixed(digits)}%` : "N/A";

const calculatePercentageChange = (newPrice, originalPrice) => {
  if (!originalPrice) return 0;
  return ((originalPrice - newPrice) / originalPrice) * 100;
};

const calculateDiscount = (pretDeVanzareRON, pretIntrareRON, dateCreated) => {
  const daysInStock = Math.floor(
    (new Date() - new Date(dateCreated)) / (1000 * 60 * 60 * 24),
  );

  let discountRate = 0;
  let color = "#16a34a";
  let level = "Nivel 1";

  if (daysInStock > 180) {
    discountRate = 0.9;
    color = "#7f1d1d";
    level = "Nivel 4";
  } else if (daysInStock > 150) {
    discountRate = 0.7;
    color = "#be123c";
    level = "Nivel 3";
  } else if (daysInStock > 120) {
    discountRate = 0.5;
    color = "#ea580c";
    level = "Nivel 2";
  } else if (daysInStock > 90) {
    discountRate = 0.3;
    color = "#ca8a04";
    level = "Nivel 1";
  }

  const profitBrut = pretDeVanzareRON - pretIntrareRON;
  const discountedPrice = profitBrut * (1 - discountRate);
  const newPrice = pretIntrareRON + discountedPrice;

  return { discountedPrice, profitBrut, newPrice, color, level };
};

const ProductDetails = ({ product, onBack }) => {
  const [taskjiraDetails, setTaskjiraDetails] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [benchmarkError, setBenchmarkError] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [analysisDetails, setAnalysisDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({ EUR: null, USD: null });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  // Vinerea Exclusivă state
  const [vxNewPrice, setVxNewPrice] = useState("");
  const [vxStartDate, setVxStartDate] = useState(null);
  const [vxEndDate, setVxEndDate] = useState(null);
  const [vxSaving, setVxSaving] = useState(false);
  const [vxMessage, setVxMessage] = useState(null);
  const [vxError, setVxError] = useState(null);
  const [vxEdited, setVxEdited] = useState(false);
  const [vxWarning, setVxWarning] = useState(null);
  const [vxAdminLink, setVxAdminLink] = useState(null);
  const [vcImagesLoading, setVcImagesLoading] = useState(false);

  useEffect(() => {
    if (product) {
      setBrand(
        product.attributes?.find((attr) => attr.name === "Designeri")
          ?.options[0] || "",
      );
      setModel(product.name.replace(/<[^>]+>/g, "")); // Remove HTML tags
      fetchTaskjiraDetails(product.sku);
      fetchAnalysisDetails(product.name);
    }
  }, [product]);

  const extractIDFromSKU = (sku) => {
    const match = sku?.match(/0*(\d+)$/);
    return match ? match[1] : null;
  };

  // Defaults for Vinerea Exclusivă when product changes
  useEffect(() => {
    if (!product) return;
    try {
      setVxEdited(false);
      setVxNewPrice(product.price || "");
      const now = dayjs();
      let nextFriday = now.day(5);
      if (now.day() > 5 || (now.day() === 5 && now.hour() >= 22)) {
        nextFriday = nextFriday.add(1, "week");
      }
      const start = nextFriday
        .hour(17)
        .minute(0)
        .second(0)
        .millisecond(0)
        .utc();
      const end = nextFriday.hour(22).minute(0).second(0).millisecond(0).utc();
      setVxStartDate(start);
      setVxEndDate(end);
      setVxMessage(null);
      setVxError(null);
    } catch (_) {
      setVxStartDate(null);
      setVxEndDate(null);
    }
  }, [product]);

  // Break-even autopopulate moved below pricingInsights definition

  useEffect(() => {
    if (!product?.sku) {
      setAnalyticsData(null);
      return;
    }

    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      try {
        const response = await axios.get(
          `https://server-9p4hx.kinsta.app/api/masterdata/product-analytics/${product.sku}`,
        );
        setAnalyticsData(response.data);
      } catch (error) {
        console.error("Error fetching analytics:", error);
        setAnalyticsError(
          error?.response?.data?.message ||
            error.message ||
            "Nu am putut încărca analytics.",
        );
        setAnalyticsData(null);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalytics();
  }, [product?.sku]);

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const [eurResp, usdResp] = await Promise.all([
          axios.get("https://api.exchangerate-api.com/v4/latest/EUR"),
          axios.get("https://api.exchangerate-api.com/v4/latest/USD"),
        ]);

        setExchangeRates({
          EUR: eurResp.data?.rates?.RON || null,
          USD: usdResp.data?.rates?.RON || null,
        });
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
      }
    };

    fetchExchangeRates();
  }, []);

  const convertToRON = (price, currency) => {
    if (price === undefined || price === null || !currency) return null;

    const numericPrice = Number(price);
    if (Number.isNaN(numericPrice)) {
      return null;
    }

    if (currency === "RON") {
      return numericPrice.toFixed(2);
    }

    if (currency === "EUR" && exchangeRates.EUR) {
      return (numericPrice * exchangeRates.EUR).toFixed(2);
    }

    if (currency === "USD" && exchangeRates.USD) {
      return (numericPrice * exchangeRates.USD).toFixed(2);
    }

    return null;
  };

  const benchmarkAvgRon = useMemo(() => {
    if (!benchmarkData?.stats?.avgPrice) return null;
    const converted = convertToRON(benchmarkData.stats.avgPrice, "USD");
    return converted ? Number(converted) : null;
  }, [benchmarkData, exchangeRates]);

  const fetchTaskjiraDetails = async (sku) => {
    try {
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/masterdata/product-details/${sku}`,
      );
      setTaskjiraDetails(response.data.taskjiraDetails);
    } catch (error) {
      console.error("Error fetching taskjira details:", error);
    }
  };

  const fetchAnalysisDetails = async (name) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/masterdata/products-details-analysis`,
        {
          params: { name },
        },
      );
      setAnalysisDetails(response.data);
    } catch (error) {
      console.error("Error fetching analysis details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateBenchmark = async () => {
    if (!product) return;

    const summary = product.name
      ? product.name.replace(/<[^>]+>/g, " ").trim()
      : "";
    const imageUrl =
      product.images && product.images.length > 0 ? product.images[0].src : "";

    if (!summary) {
      setBenchmarkError("Produsul nu are o denumire validă.");
      return;
    }

    setBenchmarkLoading(true);
    setBenchmarkError(null);

    try {
      const formData = new FormData();
      formData.append("summary", summary);
      if (imageUrl) {
        formData.append("imageUrl", imageUrl);
      }

      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/masterdata/search-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setBenchmarkData(response.data);
    } catch (error) {
      console.error("Error calculating benchmark:", error);
      const message =
        error?.response?.data?.message ||
        error.message ||
        "Eroare la calculul benchmark-ului.";
      setBenchmarkError(message);
      setBenchmarkData(null);
    } finally {
      setBenchmarkLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    const updatedName = `<strong> ${brand} </strong><br><div style="margin-top:10px; font-size:16px;">${model}</div><br>`;
    fetchAnalysisDetails(updatedName); // Refresh analysis details with the updated name
    setIsEditing(false);
  };

  const category =
    product?.categories && product?.categories.length > 0
      ? product.categories[0].name
      : "Unknown";
  const condition =
    product?.attributes?.find((attr) => attr.name === "Condiție")
      ?.options?.[0] || "N/A";
  const conditionValueVX = useMemo(() => {
    const t = String(condition || "").toLowerCase();
    if (t.includes("nou")) return 1;
    if (t.includes("excelent")) return 2;
    if (t.includes("foarte bun")) return 3;
    if (t.includes("bun")) return 4;
    if (t.includes("rezonabil")) return 5;
    return null;
  }, [condition]);
  const primaryTask = taskjiraDetails?.[0];

  const handleVinereaExclusiva = async () => {
    if (!product?.sku) return;
    const productId = extractIDFromSKU(product.sku);
    if (!productId) {
      setVxError("Nu pot determina ID-ul produsului din SKU.");
      return;
    }
    if (!vxNewPrice) {
      setVxError("Te rugăm să introduci prețul nou.");
      return;
    }

    setVxSaving(true);
    setVxMessage(null);
    setVxError(null);
    setVxWarning(null);
    setVxAdminLink(null);
    try {
      const payload = {
        productId,
        originalSku: product.sku,
        newPrice: vxNewPrice,
        startDate: vxStartDate ? vxStartDate.utc().toISOString() : null,
        endDate: vxEndDate ? vxEndDate.utc().toISOString() : null,
      };
      const resp = await axios.post(
        "https://server-9p4hx.kinsta.app/api/masterdata/vinerea-exclusiva",
        payload,
      );
      const d = resp.data || {};
      if (d.warning) {
        const parts = [];
        parts.push(d.warning);
        if (Array.isArray(d.partials) && d.partials.length) {
          parts.push(`Pași aplicați: ${d.partials.join(", ")}`);
        }
        if (d.noCloneFallback) {
          parts.push("Aplicat pe produsul original (fără clonă)");
        }
        setVxWarning(parts.join(" | "));
      }
      let okMsg = d?.sku
        ? `Operațiune reușită. SKU: ${d.sku}`
        : "Operațiune reușită.";
      if (d.reqId) {
        okMsg += ` (req: ${d.reqId})`;
      }
      setVxMessage(okMsg);
      const adminId = d?.id || productId;
      if (adminId) {
        setVxAdminLink(
          `https://www.luxuraelite.ro/wp-admin/post.php?post=${adminId}&action=edit`,
        );
      }
    } catch (err) {
      console.error("Vinerea Exclusivă error:", err);
      const respData = err?.response?.data || {};
      let eMsg =
        respData?.error || err.message || "A apărut o eroare la programare.";
      if (respData?.detail && typeof respData.detail === "string") {
        eMsg += ` | ${respData.detail}`;
      }
      if (respData?.reqId) {
        eMsg += ` (req: ${respData.reqId})`;
      }
      setVxError(eMsg);
      if (productId) {
        setVxAdminLink(
          `https://www.luxuraelite.ro/wp-admin/post.php?post=${productId}&action=edit`,
        );
        setVxWarning(
          "Serviciul a fost blocat de WAF. Poți finaliza manual din WooCommerce.",
        );
      }
    } finally {
      setVxSaving(false);
    }
  };

  const priceInsights = useMemo(() => {
    if (!primaryTask) return null;
    const costResult = calculateCost(primaryTask.pret_de_vanzare_ron, category);
    const purchaseReference = Number(costResult?.purchasePrice) || 0;
    if (!purchaseReference) return null;

    const difference =
      (Number(primaryTask.pret_intrare_ron) || 0) - purchaseReference;
    const percentage = purchaseReference
      ? (difference / purchaseReference) * 100
      : 0;

    const voucherPrice = purchaseReference * 0.95;
    const salePrice = purchaseReference * 0.9;
    const fastSalePrice = purchaseReference * 0.85;

    return {
      purchaseReference,
      difference,
      percentage,
      voucher: voucherPrice,
      sale: salePrice,
      fastSale: fastSalePrice,
    };
  }, [primaryTask, category]);

  const earliestJiraEntryDate = useMemo(() => {
    let minDate = null;

    const considerDate = (value) => {
      if (!value) return;
      const current = new Date(value);
      if (Number.isNaN(current.getTime())) return;
      if (!minDate || current < minDate) {
        minDate = current;
      }
    };

    analysisDetails?.forEach((detail) => {
      detail.JIRA?.forEach((jiraItem) => {
        considerDate(jiraItem?.entryDate);
      });
    });

    taskjiraDetails?.forEach((detail) => {
      considerDate(detail?.data_de_intrare);
    });

    return minDate ? minDate.toISOString() : null;
  }, [analysisDetails, taskjiraDetails]);

  const discountInfo = useMemo(() => {
    if (!primaryTask) return null;

    const referenceDate =
      earliestJiraEntryDate ||
      primaryTask?.data_de_intrare ||
      product?.dateCreated;
    if (!referenceDate) return null;

    return calculateDiscount(
      Number(primaryTask.pret_de_vanzare_ron) || 0,
      Number(primaryTask.pret_intrare_ron) || 0,
      referenceDate,
    );
  }, [primaryTask, earliestJiraEntryDate, product?.dateCreated]);

  const pricingInsights = useMemo(() => {
    if (!analyticsData?.priceRules) return null;

    const currentPrice = Number(analyticsData?.stockSummary?.currentPrice || 0);
    if (!currentPrice) return null;

    const initialPrice = Number(
      analyticsData.priceRules.initialPrice || currentPrice,
    );
    const priceInitialLogged = initialPrice > 0 ? initialPrice : currentPrice;
    const minimumAllowedRule = Number(
      analyticsData.priceRules.minimumAllowedPrice || 0,
    );
    const entryCost = Number(
      analyticsData.stockSummary.avgEntryPrice ||
        primaryTask?.pret_intrare_ron ||
        0,
    );
    const targetMarginPrice = Number(
      analyticsData.recommendations?.targetMarginPrice || 0,
    );
    const clearancePrice = Number(
      analyticsData.recommendations?.aggressiveClearancePrice || 0,
    );
    const breakEvenPrice = Number(
      analyticsData.recommendations?.breakEvenPrice || entryCost || 0,
    );
    const benchmarkPrice = benchmarkAvgRon || 0;
    const fallbackEntryDate =
      analyticsData.stockSummary.earliestEntry ||
      earliestJiraEntryDate ||
      primaryTask?.data_de_intrare ||
      product?.dateCreated;
    const daysInStockValue =
      analyticsData.stockSummary.daysInStock !== undefined
        ? Number(analyticsData.stockSummary.daysInStock)
        : fallbackEntryDate
          ? Math.max(
              0,
              Math.floor(
                (Date.now() - new Date(fallbackEntryDate).getTime()) /
                  (1000 * 60 * 60 * 24),
              ),
            )
          : 0;

    const luxuryMarginMultiplier = 1.2; // minimum 20% peste cost
    const costFloor = entryCost ? entryCost * luxuryMarginMultiplier : 0;

    const discountSchedule = [
      { maxDays: 90, maxDiscount: 0 },
      { maxDays: 120, maxDiscount: 0.15 },
      { maxDays: 150, maxDiscount: 0.2 },
      { maxDays: 180, maxDiscount: 0.25 },
      { maxDays: Infinity, maxDiscount: 0.25 },
    ];

    const allowedDiscount =
      discountSchedule.find((rule) => daysInStockValue <= rule.maxDays)
        ?.maxDiscount || 0;

    if (allowedDiscount === 0) {
      return {
        currentPrice,
        recommendedPrice: currentPrice,
        reductionAmount: 0,
        reductionPercent: 0,
        minimumAllowed: priceInitialLogged,
        rotationFloor: priceInitialLogged,
        costFloor,
        benchFloor: benchmarkPrice || null,
        targetMarginPrice: targetMarginPrice || null,
        clearancePrice: clearancePrice || null,
        benchmarkPrice: benchmarkPrice || null,
        breakEvenPrice: breakEvenPrice || null,
        initialPrice: priceInitialLogged || null,
        allowedDiscountPercent: 0,
      };
    }

    const minimumAllowed = Math.max(
      minimumAllowedRule || 0,
      priceInitialLogged * 0.75,
    );

    const profitBrut = Math.max(0, currentPrice - entryCost);
    const policyPrice = entryCost + profitBrut * (1 - allowedDiscount);
    const rotationFloor = Math.max(
      policyPrice,
      priceInitialLogged * (1 - allowedDiscount),
    );

    const benchmarkFloor = benchmarkPrice ? benchmarkPrice * 0.95 : 0;
    const clearanceCandidate = clearancePrice || 0;
    const targetCandidate = targetMarginPrice || 0;

    const floors = [
      minimumAllowed,
      rotationFloor,
      costFloor,
      benchmarkFloor,
      breakEvenPrice ? breakEvenPrice * 1.1 : 0,
    ].filter((value) => value && value > 0);

    let recommendedPrice = Math.max(...floors, entryCost);

    const optionalTargets = [targetCandidate, clearanceCandidate]
      .filter((value) => value && value > 0)
      .map((value) => Math.max(value, recommendedPrice));

    if (optionalTargets.length > 0) {
      const viableTargets = optionalTargets.filter(
        (value) => value <= currentPrice,
      );
      if (viableTargets.length > 0) {
        recommendedPrice = Math.min(...viableTargets, recommendedPrice);
      }
    }

    recommendedPrice = Math.max(
      recommendedPrice,
      minimumAllowed,
      costFloor,
      policyPrice,
    );
    recommendedPrice = Math.min(recommendedPrice, currentPrice);

    const reductionAmount = Math.max(0, currentPrice - recommendedPrice);
    const reductionPercent = currentPrice
      ? Math.round((reductionAmount / currentPrice) * 1000) / 10
      : 0;

    return {
      currentPrice,
      recommendedPrice,
      reductionAmount,
      reductionPercent,
      minimumAllowed,
      rotationFloor,
      costFloor,
      benchFloor: benchmarkFloor || null,
      targetMarginPrice: targetMarginPrice || null,
      clearancePrice: clearancePrice || null,
      benchmarkPrice: benchmarkPrice || null,
      breakEvenPrice: breakEvenPrice || null,
      initialPrice: priceInitialLogged || null,
      allowedDiscountPercent: Math.round(allowedDiscount * 100),
      policyPrice,
      profitBrut,
    };
  }, [
    analyticsData,
    benchmarkAvgRon,
    primaryTask?.pret_intrare_ron,
    earliestJiraEntryDate,
    product?.dateCreated,
  ]);

  // Populate Preț nou from Break-even when available, unless user edited
  useEffect(() => {
    if (
      !vxEdited &&
      pricingInsights?.breakEvenPrice &&
      pricingInsights.breakEvenPrice > 0
    ) {
      try {
        const be = Number(pricingInsights.breakEvenPrice);
        if (Number.isFinite(be) && be > 0) {
          setVxNewPrice(String(Math.round(be)));
        }
      } catch (_) {}
    }
  }, [pricingInsights, vxEdited]);

  if (!product) return null;

  const productEntryDate =
    earliestJiraEntryDate ||
    primaryTask?.data_de_intrare ||
    product.dateCreated;
  const primaryEntryDateLabel = primaryTask?.data_de_intrare
    ? new Date(primaryTask.data_de_intrare).toLocaleDateString()
    : "N/A";
  const displayEntryDate = productEntryDate
    ? new Date(productEntryDate).toLocaleDateString()
    : "N/A";
  const price = product.price;
  const salePrice = product.salePrice !== "N/A" ? product.salePrice : null;
  const daysInStock = productEntryDate
    ? Math.floor(
        (new Date() - new Date(productEntryDate)) / (1000 * 60 * 60 * 24),
      )
    : 0;
  const productImage =
    product.images && product.images.length > 0 ? product.images[0].src : "";

  const handleBackClick = () => {
    onBack();
  };

  const calculateMetrics = (details) => {
    return details.map((detail) => {
      const entryPrice = detail.JIRA?.[0]?.entryPrice || 0;
      const pretDeVanzare = detail.JIRA?.[0]?.pret_de_vanzare_ron || 0;
      const dataIntrare = new Date(detail.JIRA?.[0]?.entryDate).getTime();
      const dataVandut = new Date(
        detail.JIRA?.[0]?.data_vandut ||
          detail.sold?.[0]?.invoiceDate ||
          new Date(),
      ).getTime();
      const daysInStock = (dataVandut - dataIntrare) / (1000 * 60 * 60 * 24);

      const marjaBruta = pretDeVanzare
        ? ((pretDeVanzare - entryPrice) / pretDeVanzare) * 100
        : 0;
      const roi = entryPrice
        ? ((pretDeVanzare - entryPrice) / entryPrice) * 100
        : 0;
      const gain = pretDeVanzare - entryPrice;

      return {
        sku: detail.sku,
        conditie: detail.conditie,
        marjaBruta: isNaN(marjaBruta) ? 0 : marjaBruta,
        roi: isNaN(roi) ? 0 : roi,
        gain: isNaN(gain) ? 0 : gain,
        daysInStock: isNaN(daysInStock) ? 0 : daysInStock,
        entryPrice,
        pretDeVanzare,
      };
    });
  };

  const calculatedDetails = calculateMetrics(analysisDetails || []);
  const averages = calculatedDetails.reduce(
    (acc, detail, _, { length }) => {
      acc.marjaBruta += detail.marjaBruta / length;
      acc.roi += detail.roi / length;
      acc.gain += detail.gain / length;
      acc.daysInStock += detail.daysInStock / length;
      return acc;
    },
    { marjaBruta: 0, roi: 0, gain: 0, daysInStock: 0 },
  );

  const handleCreateLabel = async () => {
    try {
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/masterdata/create-label",
        { sku: product.sku },
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      saveAs(blob, `${product.sku}-label.pdf`);
      alert("Eticheta a fost creată cu succes!");
    } catch (error) {
      console.error("Error creating label:", error);
      alert("Eroare la crearea etichetei.");
    }
  };

  const handleCreateCard = async () => {
    try {
      const cardResponse = await axios.post(
        "https://server-9p4hx.kinsta.app/api/masterdata/create-card-pdf",
        { sku: product.sku },
        {
          responseType: "blob",
        },
      );
      const cardBlob = new Blob([cardResponse.data], {
        type: "application/pdf",
      });
      saveAs(cardBlob, `${product.sku}-card.pdf`);

      const entrupyResponse = await axios.post(
        "https://server-9p4hx.kinsta.app/api/masterdata/create-entrupy-pdf",
        { sku: product.sku },
        {
          responseType: "blob",
        },
      );
      const entrupyBlob = new Blob([entrupyResponse.data], {
        type: "application/pdf",
      });
      saveAs(entrupyBlob, `${product.sku}-entrupy.pdf`);

      alert("Cardurile au fost create cu succes!");
    } catch (error) {
      console.error("Error creating cards:", error);
      alert("Eroare la crearea cardurilor.");
    }
  };

  const handleDownloadVcImages = async () => {
    const webImages = Array.isArray(product?.images)
      ? product.images.filter((image) => image?.src)
      : [];

    if (!webImages.length) {
      alert("Produsul nu are imagini web disponibile.");
      return;
    }

    setVcImagesLoading(true);

    try {
      const response = await axios.post(
        `${VC_IMAGE_ZIP_API_BASE_URL}/api/tasks/product-images-zip`,
        {
          productId: product?.id || null,
          sku: product?.sku || null,
          productUrl: product?.permalink || null,
          productName: model || product?.name || null,
          imageUrls: webImages.map((image) => image.src),
        },
        {
          responseType: "blob",
        },
      );

      const contentDisposition = response.headers["content-disposition"] || "";
      const encodedNameMatch = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)/i,
      );
      const plainNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const suggestedFileName = encodedNameMatch
        ? decodeURIComponent(encodedNameMatch[1])
        : plainNameMatch?.[1] || `${product?.sku || "produs"}-imagini-vc.zip`;

      saveAs(response.data, suggestedFileName);
    } catch (error) {
      console.error("Error downloading VC images:", error);
      alert("Nu am putut descărca arhiva de imagini de pe serverul local.");
    } finally {
      setVcImagesLoading(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#f4f6fb",
        minHeight: "100vh",
        py: 4,
        px: { xs: 2, md: 5 },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBackIosNewIcon />}
          onClick={handleBackClick}
        >
          Înapoi la lista de produse
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCreateLabel}
            startIcon={<LabelIcon />}
          >
            Crează eticheta
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreateCard}
            startIcon={<CardIcon />}
          >
            Crează cardurile
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleDownloadVcImages}
            startIcon={<CollectionsIcon />}
            disabled={vcImagesLoading}
          >
            {vcImagesLoading ? "Se descarcă..." : "Imagini VC"}
          </Button>
        </Stack>
      </Stack>

      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)",
          background: "linear-gradient(135deg, #ffffff 0%, #eff3ff 100%)",
          mb: 4,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                position: "relative",
                borderRadius: 3,
                overflow: "hidden",
                minHeight: 220,
                backgroundColor: "#f8fafc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
              }}
            >
              {productImage ? (
                <img
                  src={productImage}
                  alt={model}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Typography variant="subtitle1" color="text.secondary">
                  Fără imagine
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            {isEditing ? (
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    fullWidth
                  />
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                  >
                    Salvează
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: "#0f172a" }}
                  >
                    {brand || "Brand necunoscut"}
                  </Typography>
                  <Chip label={condition} color="primary" variant="outlined" />
                  {primaryTask?.tip_achizitie && (
                    <Chip
                      label={`Tip achiziție: ${primaryTask.tip_achizitie}`}
                      color="secondary"
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                    />
                  )}
                </Stack>
                <Typography
                  variant="h5"
                  fontWeight={500}
                  color="text.secondary"
                >
                  {model}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`SKU: ${product.sku}`} size="small" />
                  <Chip label={`Categorie: ${category}`} size="small" />
                  <Chip label={`Zile în stoc: ${daysInStock}`} size="small" />
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Preț listat
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ color: "#1e293b" }}
                    >
                      {price} RON
                    </Typography>
                    {salePrice && (
                      <Typography variant="body2" color="error">
                        Reducere: {salePrice} RON
                      </Typography>
                    )}
                  </Stack>
                  <Button
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    onClick={handleEdit}
                  >
                    Editează datele
                  </Button>
                </Stack>
              </Stack>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Card: Programare Vinerea Exclusivă */}
      <Paper
        sx={{
          p: 0,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(15, 23, 42, 0.12)",
          mb: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f5f7ff 100%)",
          border: "1px solid #e2e8f0",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(120deg, #0ea5e9 0%, #6366f1 100%)",
            color: "white",
            p: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "grid",
                placeItems: "center",
                borderRadius: 2,
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.25)",
                backdropFilter: "blur(6px)",
              }}
            >
              <BoltIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                Vinerea exclusivă
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Clonează produsul și setează prețul promoțional 17:00–22:00
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Chip
              icon={<AccessTimeIcon sx={{ color: "white!important" }} />}
              label="17:00 – 22:00"
              sx={{ background: "rgba(255,255,255,0.18)", color: "white" }}
            />
            {vxStartDate && (
              <Chip
                icon={<EventAvailableIcon sx={{ color: "white!important" }} />}
                label={dayjs(vxStartDate).local().format("DD MMM YYYY")}
                sx={{ background: "rgba(255,255,255,0.18)", color: "white" }}
              />
            )}
          </Stack>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                label="Preț actual"
                value={product?.price || ""}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">RON</InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Preț nou"
                value={vxNewPrice}
                onChange={(e) => {
                  setVxEdited(true);
                  setVxNewPrice(e.target.value);
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">RON</InputAdornment>
                  ),
                }}
                helperText="Introduceți prețul promoțional pentru fereastra Vinerea exclusivă"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Data (Start)"
                  value={vxStartDate}
                  onChange={(v) => setVxStartDate(v ? dayjs(v).utc() : null)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Data (Sfârșit)"
                  value={vxEndDate}
                  onChange={(v) => setVxEndDate(v ? dayjs(v).utc() : null)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Intervalul orar se aplică automat pentru data selectată.">
                  <Chip
                    icon={<LocalOfferIcon />}
                    label="Fereastră promo: 17:00–22:00"
                  />
                </Tooltip>
                <Chip
                  label={`SKU: ${product?.sku || "-"}`}
                  variant="outlined"
                />
                {conditionValueVX !== null && (
                  <Tooltip title="Preview top descriere la clonă">
                    <Chip
                      color="primary"
                      variant="outlined"
                      label={`[range_shortcode val="${conditionValueVX}"]`}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Grid>
          </Grid>

          {vxError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {vxError}
            </Alert>
          )}
          {vxWarning && (
            <Alert
              severity="warning"
              sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              {vxWarning}
              {vxAdminLink && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  sx={{ ml: 1 }}
                  component="a"
                  href={vxAdminLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Deschide în Woo Admin
                </Button>
              )}
            </Alert>
          )}
          {vxMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {vxMessage}
            </Alert>
          )}

          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ mt: 2, pb: 1.5, px: 0.5 }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleVinereaExclusiva}
              disabled={vxSaving || !vxNewPrice || isNaN(Number(vxNewPrice))}
              startIcon={<BoltIcon />}
              sx={{ px: 3, fontWeight: 700 }}
            >
              {vxSaving ? "Se programează..." : "Programare Vinerea exclusivă"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {analyticsLoading && (
        <Paper
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 3,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={32} />
        </Paper>
      )}

      {analyticsError && (
        <Paper
          sx={{ mt: 3, p: 3, borderRadius: 3, border: "1px solid #fecaca" }}
        >
          <Typography variant="body2" color="error">
            {analyticsError}
          </Typography>
        </Paper>
      )}

      {analyticsData && (
        <>
          {pricingInsights && (
            <Paper
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 3,
                boxShadow: "0 16px 40px rgba(22, 78, 99, 0.2)",
                background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
                color: "white",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.7 }}>
                    Recomandare discount premium
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    Vinde la {formatCurrency(pricingInsights.recommendedPrice)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
                    Propunere bazată pe costuri, reguli interne și prețuri de
                    piață pentru a păstra percepția de lux.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <Chip
                    label={`Reducere propusă: ${pricingInsights.reductionPercent.toFixed(1)}%`}
                    color={
                      pricingInsights.reductionPercent > 0
                        ? "warning"
                        : "default"
                    }
                    sx={{ color: "white", fontWeight: 600 }}
                  />
                  <Chip
                    label={`Minim permis: ${formatCurrency(pricingInsights.minimumAllowed)}`}
                    variant="outlined"
                    sx={{
                      borderColor: "rgba(255,255,255,0.4)",
                      color: "white",
                    }}
                  />
                </Stack>
              </Stack>
              <Divider
                sx={{ mt: 2, mb: 2, borderColor: "rgba(255,255,255,0.2)" }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Preț curent
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(pricingInsights.currentPrice)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Diferență propusă
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {pricingInsights.reductionAmount > 0
                      ? `-${formatCurrency(pricingInsights.reductionAmount)}`
                      : "Nicio reducere sugerată"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Repere folosite
                  </Typography>
                  <Stack spacing={0.5}>
                    {pricingInsights.targetMarginPrice && (
                      <Typography variant="body2">
                        • Marjă țintă:{" "}
                        {formatCurrency(pricingInsights.targetMarginPrice)}
                      </Typography>
                    )}
                    {pricingInsights.benchmarkPrice && (
                      <Typography variant="body2">
                        • Media piață lux:{" "}
                        {formatCurrency(pricingInsights.benchmarkPrice)}
                      </Typography>
                    )}
                    {pricingInsights.breakEvenPrice && (
                      <Typography variant="body2">
                        • Break-even:{" "}
                        {formatCurrency(pricingInsights.breakEvenPrice)}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      • Discount maxim (politică{" "}
                      {pricingInsights.allowedDiscountPercent}% din profit):{" "}
                      {formatCurrency(pricingInsights.policyPrice)}
                    </Typography>
                    <Typography variant="body2">
                      • Prag minim intern:{" "}
                      {formatCurrency(pricingInsights.minimumAllowed)}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Typography variant="overline" sx={{ mt: 4, color: "primary.main" }}>
            Panorama stoc & performanță
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: "0 12px 24px rgba(15, 23, 42, 0.05)",
                  height: "100%",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Stoc & expunere
                </Typography>
                <Stack spacing={1.2}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Unități pe stoc
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {analyticsData.stockSummary.onHand}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Cost mediu intrare
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(analyticsData.stockSummary.avgEntryPrice)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Valoare stoc (RON)
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(analyticsData.stockSummary.stockValue)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Zile în stoc
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {analyticsData.stockSummary.daysInStock}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Dată intrare inițială
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(analyticsData.stockSummary.earliestEntry)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: "0 12px 24px rgba(15, 23, 42, 0.05)",
                  height: "100%",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Viteză & sell-through
                </Typography>
                <Stack spacing={1.2}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Vândute total
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {analyticsData.salesMetrics.totalSold}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Vândute în ultimele 30z
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {analyticsData.salesMetrics.last30Days.count}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Medie/zi (30z)
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {analyticsData.salesMetrics.avgDailySales}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Sell-through
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatPercent(analyticsData.salesMetrics.sellThrough)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Ultima vânzare
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(analyticsData.salesMetrics.lastSaleDate)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: "0 12px 24px rgba(15, 23, 42, 0.05)",
                  height: "100%",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Financiare & recomandări
                </Typography>
                <Stack spacing={1.2}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Venit potențial
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(
                        analyticsData.financials.potentialRevenue,
                      )}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Profit potențial
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(analyticsData.financials.potentialProfit)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Marjă pe stoc
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatPercent(analyticsData.financials.marginOnHand, 1)}
                    </Typography>
                  </Stack>
                  {analyticsData.recommendations && (
                    <>
                      {analyticsData.recommendations.breakEvenPrice && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Prag break-even
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {formatCurrency(
                              analyticsData.recommendations.breakEvenPrice,
                            )}
                          </Typography>
                        </Stack>
                      )}
                      {analyticsData.recommendations.targetMarginPrice && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            25% margină
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {formatCurrency(
                              analyticsData.recommendations.targetMarginPrice,
                            )}
                          </Typography>
                        </Stack>
                      )}
                      {analyticsData.recommendations
                        .aggressiveClearancePrice && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Clear-out recomandat
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {formatCurrency(
                              analyticsData.recommendations
                                .aggressiveClearancePrice,
                            )}
                          </Typography>
                        </Stack>
                      )}
                    </>
                  )}
                  {analyticsData.priceRules && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Reguli discount
                      </Typography>
                      <Stack spacing={0.8} sx={{ mt: 0.5 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Preț inițial listat
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {formatCurrency(
                              analyticsData.priceRules.initialPrice,
                            )}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Minim permis (25% din inițial)
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {formatCurrency(
                              analyticsData.priceRules.minimumAllowedPrice,
                            )}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Preț curent
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            color={
                              analyticsData.priceRules.isBelowMinimum
                                ? "error.main"
                                : "text.primary"
                            }
                          >
                            {formatCurrency(
                              analyticsData.priceRules.currentPrice,
                            )}
                          </Typography>
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: "0 12px 24px rgba(15, 23, 42, 0.05)",
                  height: "100%",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Istoric prețuri
                </Typography>
                {analyticsData.priceHistory &&
                analyticsData.priceHistory.length > 0 ? (
                  <Stack spacing={1.2}>
                    {analyticsData.priceHistory.map((item, index) => (
                      <Stack
                        key={`${item.updatedAt}-${index}`}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          borderBottom:
                            index === analyticsData.priceHistory.length - 1
                              ? "none"
                              : "1px solid #e2e8f0",
                          pb: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(item.newPrice)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Din {formatCurrency(item.oldPrice)}
                          </Typography>
                          {item.source && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Sursă: {item.source}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(item.updatedAt)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nu există modificări de preț salvate.
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: "0 12px 24px rgba(15, 23, 42, 0.05)",
                  height: "100%",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Semnale de risc
                </Typography>
                {analyticsData.riskFlags &&
                analyticsData.riskFlags.length > 0 ? (
                  <Stack spacing={1.2}>
                    {analyticsData.riskFlags.map((flag, index) => (
                      <Chip
                        key={`${flag.message}-${index}`}
                        label={flag.message}
                        color={
                          flag.severity === "high"
                            ? "error"
                            : flag.severity === "medium"
                              ? "warning"
                              : "default"
                        }
                        variant={
                          flag.severity === "low" ? "outlined" : "filled"
                        }
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nu există riscuri semnificative identificate.
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
              height: "100%",
            }}
          >
            <Typography variant="overline" color="primary">
              Rezumat produs
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Data intrare (JIRA)
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {displayEntryDate}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Zile în stoc
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {daysInStock}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Categoria
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {category}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
              height: "100%",
            }}
          >
            <Typography variant="overline" color="primary">
              Snapshot achiziție
            </Typography>
            <Divider sx={{ my: 2 }} />
            {primaryTask ? (
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Preț intrare
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {primaryTask.pret_intrare_ron} RON
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Preț vânzare propus
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {primaryTask.pret_de_vanzare_ron} RON
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Nr. jurnal
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {primaryTask.nr_jurnal_special || "N/A"}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Dată intrare referință
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {primaryEntryDateLabel}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nu există încă date de achiziție.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
              height: "100%",
            }}
          >
            <Typography variant="overline" color="primary">
              Benchmark extern
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleCalculateBenchmark}
                disabled={benchmarkLoading}
              >
                {benchmarkLoading ? "Se calculează..." : "Calculează benchmark"}
              </Button>
              {benchmarkError && (
                <Typography variant="body2" color="error">
                  {benchmarkError}
                </Typography>
              )}
              {benchmarkData && (
                <Stack spacing={2}>
                  {benchmarkData.stats && (
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2">
                        Rezumat statistici
                      </Typography>
                      <Typography variant="body2">
                        Min: {benchmarkData.stats.minPrice ?? "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        Max: {benchmarkData.stats.maxPrice ?? "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        Mediană: {benchmarkData.stats.median ?? "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        Medie: {benchmarkData.stats.avgPrice ?? "N/A"}
                      </Typography>
                    </Stack>
                  )}
                  {Array.isArray(benchmarkData.results) &&
                    benchmarkData.results.length > 0 && (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          Listări relevante
                        </Typography>
                        {benchmarkData.results
                          .slice(0, 3)
                          .map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: "#f8fafc",
                              }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {item.aiResult?.title ||
                                  item.title ||
                                  "Listing"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.priceValue && item.aiResult?.currency
                                  ? `${item.priceValue} ${item.aiResult.currency}`
                                  : item.aiResult?.price || item.price || "N/A"}
                              </Typography>
                              {item.link && (
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={item.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ textDecoration: "underline" }}
                                >
                                  Vezi detalii
                                </Typography>
                              )}
                            </Box>
                          ))}
                      </Stack>
                    )}
                  {Array.isArray(benchmarkData.pureResaleResultsUsed) &&
                    benchmarkData.pureResaleResultsUsed.length > 0 && (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          Pure resale results used for stats
                        </Typography>
                        {benchmarkData.pureResaleResultsUsed.map(
                          (item, index) => {
                            const ai = item.aiResult || {};
                            const priceValue = item.priceValue;
                            const currency = ai.currency;
                            const convertedPrice = convertToRON(
                              priceValue,
                              currency,
                            );
                            return (
                              <Box
                                key={index}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  bgcolor: "#eef2ff",
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {ai.title || item.title || "Listing"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {convertedPrice !== null
                                    ? `${convertedPrice} RON`
                                    : priceValue && currency
                                      ? `${priceValue} ${currency}`
                                      : ai.price || item.price || "N/A"}
                                </Typography>
                                {item.link && (
                                  <Typography
                                    variant="body2"
                                    component="a"
                                    href={item.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ textDecoration: "underline" }}
                                  >
                                    Vezi detalii
                                  </Typography>
                                )}
                              </Box>
                            );
                          },
                        )}
                      </Stack>
                    )}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {taskjiraDetails && (
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)",
            mt: 4,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Detalii achiziție
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {taskjiraDetails.map((detail, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    height: "100%",
                    bgcolor: "#f8fafc",
                  }}
                  elevation={0}
                >
                  <Stack spacing={1.2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {detail.tip_achizitie}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Data de intrare:</strong>{" "}
                      {new Date(detail.data_de_intrare).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Preț intrare:</strong> {detail.pret_intrare_ron}{" "}
                      RON
                    </Typography>
                    <Typography variant="body2">
                      <strong>Preț vânzare:</strong>{" "}
                      {detail.pret_de_vanzare_ron} RON
                    </Typography>
                    <Typography variant="body2">
                      <strong>Nr. Jurnal special:</strong>{" "}
                      {detail.nr_jurnal_special}
                    </Typography>
                    <Typography variant="body2">
                      <strong>SKU:</strong> {detail.sku}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
            {priceInsights && discountInfo && (
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        boxShadow: "0 18px 50px rgba(22, 163, 74, 0.18)",
                        height: "100%",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={3}
                        alignItems="center"
                      >
                        <Box sx={{ width: 120, height: 120 }}>
                          <CircularProgressbar
                            value={Math.min(
                              Math.abs(priceInsights.percentage),
                              100,
                            )}
                            text={`${priceInsights.percentage > 0 ? "+" : ""}${priceInsights.percentage.toFixed(2)}%`}
                            styles={buildStyles({
                              pathColor:
                                priceInsights.percentage <= 0
                                  ? "#16a34a"
                                  : "#ef4444",
                              textColor:
                                priceInsights.percentage <= 0
                                  ? "#16a34a"
                                  : "#ef4444",
                              trailColor: "#e2e8f0",
                            })}
                          />
                        </Box>
                        <Stack spacing={0.5}>
                          <Typography variant="h6" fontWeight={600}>
                            Preț achiziție
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {priceInsights.difference > 0 ? (
                              <ArrowDropUpIcon color="error" />
                            ) : (
                              <ArrowDropDownIcon color="success" />
                            )}
                            {priceInsights.difference > 0 ? "+" : ""}
                            {formatNumber(priceInsights.difference)} RON
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Maxim CN:{" "}
                            {formatNumber(priceInsights.purchaseReference)} RON
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        boxShadow: "0 18px 50px rgba(15, 23, 42, 0.12)",
                        height: "100%",
                      }}
                    >
                      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                        Scenarii de preț
                      </Typography>
                      <Stack spacing={1.5}>
                        {[
                          {
                            label: "Preț Voucher",
                            value: priceInsights.voucher,
                            delta: calculatePercentageChange(
                              priceInsights.voucher,
                              priceInsights.purchaseReference,
                            ),
                          },
                          {
                            label: "Preț Vânzare",
                            value: priceInsights.sale,
                            delta: calculatePercentageChange(
                              priceInsights.sale,
                              priceInsights.purchaseReference,
                            ),
                          },
                          {
                            label: "Preț FAST Sale",
                            value: priceInsights.fastSale,
                            delta: calculatePercentageChange(
                              priceInsights.fastSale,
                              priceInsights.purchaseReference,
                            ),
                          },
                        ].map((option, idx) => {
                          const deltaColor =
                            option.delta >= 0 ? "#ef4444" : "#16a34a";
                          const DeltaIcon =
                            option.delta >= 0
                              ? ArrowDropUpIcon
                              : ArrowDropDownIcon;
                          return (
                            <Stack
                              key={idx}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>{option.label}:</strong>{" "}
                                {formatNumber(option.value)} RON
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.3}
                                alignItems="center"
                              >
                                <DeltaIcon sx={{ color: deltaColor }} />
                                <Typography
                                  variant="body2"
                                  sx={{ color: deltaColor }}
                                >
                                  {`${option.delta > 0 ? "+" : ""}${option.delta.toFixed(2)}%`}
                                </Typography>
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        boxShadow: "0 18px 50px rgba(34, 197, 94, 0.18)",
                        height: "100%",
                        position: "relative",
                      }}
                    >
                      <Chip
                        label={discountInfo.level}
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                          backgroundColor: discountInfo.color,
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                        Politica de reduceri
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong style={{ color: discountInfo.color }}>
                          Preț nou:
                        </strong>{" "}
                        <span style={{ fontSize: "1.5rem" }}>
                          {formatNumber(discountInfo.newPrice)} RON
                        </span>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Profit cu discount:{" "}
                        <strong>
                          {formatNumber(discountInfo.discountedPrice)} RON
                        </strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Profit brut: {formatNumber(discountInfo.profitBrut)} RON
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)",
          mt: 4,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Analiză internă
        </Typography>
        <Divider sx={{ my: 2 }} />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell>Condiție</TableCell>
                    <TableCell>Marja brută</TableCell>
                    <TableCell>ROI</TableCell>
                    <TableCell>Profit</TableCell>
                    <TableCell>Durată în stoc</TableCell>
                    <TableCell>Preț de intrare</TableCell>
                    <TableCell>Preț de vânzare</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calculatedDetails.map((detail, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{detail.sku}</TableCell>
                      <TableCell>{detail.conditie}</TableCell>
                      <TableCell>{detail.marjaBruta.toFixed(2)}%</TableCell>
                      <TableCell>{detail.roi.toFixed(2)}%</TableCell>
                      <TableCell>{detail.gain.toFixed(2)} RON</TableCell>
                      <TableCell>
                        {detail.daysInStock.toFixed(2)} zile
                      </TableCell>
                      <TableCell>{detail.entryPrice.toFixed(2)} RON</TableCell>
                      <TableCell>
                        {detail.pretDeVanzare.toFixed(2)} RON
                      </TableCell>
                    </TableRow>
                  ))}
                  {calculatedDetails.length > 0 && (
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell>
                        <strong>Medie</strong>
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell>{averages.marjaBruta.toFixed(2)}%</TableCell>
                      <TableCell>{averages.roi.toFixed(2)}%</TableCell>
                      <TableCell>{averages.gain.toFixed(2)} RON</TableCell>
                      <TableCell>
                        {averages.daysInStock.toFixed(2)} zile
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {analysisDetails && analysisDetails.length > 0 && (
              <Grid container spacing={3} sx={{ mt: 2 }}>
                {analysisDetails.map((detail, index) => (
                  <Grid item xs={12} key={index}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        boxShadow: "0 12px 24px rgba(15, 23, 42, 0.05)",
                      }}
                    >
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <Box
                            sx={{
                              position: "relative",
                              borderRadius: 3,
                              overflow: "hidden",
                              background: "#f8fafc",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 2,
                              minHeight: 160,
                            }}
                          >
                            <img
                              src={detail.dfImageLink}
                              alt="Product"
                              style={{
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: "12px",
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <Stack spacing={1}>
                            <Typography variant="h6" fontWeight={600}>
                              {model}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                label={`Condiție: ${detail.conditie}`}
                                size="small"
                              />
                              <Chip label={`SKU: ${detail.sku}`} size="small" />
                              <Chip
                                label={`Creat: ${new Date(
                                  detail.dateCreated,
                                ).toLocaleDateString()}`}
                                size="small"
                              />
                            </Stack>
                            <Stack direction="row" spacing={3}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Preț listat
                                </Typography>
                                <Typography variant="h6">
                                  {formatNumber(detail.price)} RON
                                </Typography>
                              </Box>
                              {detail.salePrice && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Preț reducere
                                  </Typography>
                                  <Typography variant="h6" color="error">
                                    {formatNumber(detail.salePrice)} RON
                                  </Typography>
                                </Box>
                              )}
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Pe stoc
                                </Typography>
                                <Typography variant="h6">
                                  {detail.inStock}
                                </Typography>
                              </Box>
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Stack spacing={2}>
                            {detail.sold && detail.sold.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Date vânzări
                                </Typography>
                                <Stack spacing={0.5}>
                                  {detail.sold.map((soldItem, soldIndex) => (
                                    <Typography key={soldIndex} variant="body2">
                                      {new Date(
                                        soldItem.invoiceDate,
                                      ).toLocaleDateString()}{" "}
                                      - {formatNumber(soldItem.subtotal)} RON
                                    </Typography>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                            {detail.JIRA && detail.JIRA.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Detalii JIRA
                                </Typography>
                                <Stack spacing={0.5}>
                                  {detail.JIRA.map((jiraItem, jiraIndex) => (
                                    <Typography key={jiraIndex} variant="body2">
                                      {new Date(
                                        jiraItem.entryDate,
                                      ).toLocaleDateString()}{" "}
                                      | {formatNumber(jiraItem.entryPrice)} RON
                                    </Typography>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Paper>

      <Box sx={{ mt: 4 }}>
        <WooCommerceProductDetails
          sku={product.sku}
          journalNumber={primaryTask?.nr_jurnal_special}
        />
      </Box>
    </Box>
  );
};

export default ProductDetails;
